import * as React from 'react';
import { cn } from '@/lib/utils';

/* ─── Types ────────────────────────────────────────────────────────────────── */

interface MarkdownViewerProps extends React.HTMLAttributes<HTMLDivElement> {
  content: string;
}

/* ─── Inline markdown parser ──────────────────────────────────────────────── */

function formatInline(text: string): React.ReactNode[] {
  // Parse **bold**, *italic*, `code`, and B-NNN links
  const parts: React.ReactNode[] = [];
  // Regex: **bold** | *italic* | `code` | B-NNN
  const regex = /(\*\*(.+?)\*\*)|(\*(.+?)\*)|(`(.+?)`)|(\b(B-\d{3,})\b)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    // Text before match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    if (match[1]) {
      // **bold**
      parts.push(<strong key={key++} className="font-semibold text-raios-text">{match[2]}</strong>);
    } else if (match[3]) {
      // *italic*
      parts.push(<em key={key++} className="italic">{match[4]}</em>);
    } else if (match[5]) {
      // `code`
      parts.push(
        <code key={key++} className="font-mono text-raios-accent-text bg-raios-elevated px-1 py-0.5 rounded text-[0.85em]">
          {match[6]}
        </code>
      );
    } else if (match[7]) {
      // B-NNN
      parts.push(
        <span key={key++} className="font-mono text-raios-accent-text bg-raios-accent/10 px-1 py-0.5 rounded text-[0.85em] cursor-pointer hover:bg-raios-accent/20 transition-colors">
          {match[8]}
        </span>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}

/* ─── Component ────────────────────────────────────────────────────────────── */

const MarkdownViewer = React.forwardRef<HTMLDivElement, MarkdownViewerProps>(
  ({ content, className, ...props }, ref) => {
    const rendered = React.useMemo(() => {
      const lines = content.split('\n');
      const elements: React.ReactNode[] = [];
      let inCodeBlock = false;
      let codeLines: string[] = [];

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        // Code block
        if (line.startsWith('```')) {
          if (inCodeBlock) {
            elements.push(
              <pre
                key={`code-${i}`}
                className="bg-raios-surface border border-raios-border-muted rounded-lg p-3 overflow-x-auto font-mono text-xs text-raios-text-secondary my-2"
              >
                <code>{codeLines.join('\n')}</code>
              </pre>
            );
            codeLines = [];
            inCodeBlock = false;
          } else {
            inCodeBlock = true;
          }
          continue;
        }

        if (inCodeBlock) {
          codeLines.push(line);
          continue;
        }

        // Headings
        if (line.startsWith('### ')) {
          elements.push(
            <h3 key={i} className="text-base font-semibold text-raios-text mt-4 mb-1">
              {formatInline(line.slice(4))}
            </h3>
          );
          continue;
        }
        if (line.startsWith('## ')) {
          elements.push(
            <h2 key={i} className="text-lg font-semibold text-raios-text mt-5 mb-1.5">
              {formatInline(line.slice(3))}
            </h2>
          );
          continue;
        }
        if (line.startsWith('# ')) {
          elements.push(
            <h1 key={i} className="text-xl font-bold text-raios-text mt-6 mb-2">
              {formatInline(line.slice(2))}
            </h1>
          );
          continue;
        }

        // Horizontal rule
        if (line.match(/^-{3,}$/)) {
          elements.push(
            <hr key={i} className="border-raios-border-muted my-4" />
          );
          continue;
        }

        // List items
        if (line.match(/^[\s]*[-*]\s/)) {
          const indentMatch = line.match(/^(\s*)/);
          const indent = indentMatch ? Math.floor(indentMatch[1].length / 2) : 0;
          const text = line.replace(/^[\s]*[-*]\s/, '');
          elements.push(
            <div
              key={i}
              className="flex items-start gap-2 text-sm text-raios-text-secondary leading-relaxed"
              style={{ paddingLeft: `${indent * 16 + 8}px` }}
            >
              <span className="text-raios-muted mt-1.5 shrink-0">&#8226;</span>
              <span>{formatInline(text)}</span>
            </div>
          );
          continue;
        }

        // Bold: **text**
        // Inline code: `text`
        // Empty line
        if (line.trim() === '') {
          elements.push(<div key={i} className="h-2" />);
          continue;
        }

        // Regular paragraph with inline formatting
        elements.push(
          <p key={i} className="text-sm text-raios-text-secondary leading-relaxed">
            {formatInline(line)}
          </p>
        );
      }

      return elements;
    }, [content]);

    return (
      <div
        ref={ref}
        className={cn(
          'prose-raios flex flex-col gap-0.5',
          className
        )}
        {...props}
      >
        {rendered}
      </div>
    );
  }
);

MarkdownViewer.displayName = 'MarkdownViewer';

export { MarkdownViewer };
export type { MarkdownViewerProps };
